import React, { useEffect, useState } from 'react';
import { DEVICE_TYPE, PAGE_LAYOUT_TYPE } from 'src/app/slice';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { ImageProps } from 'src/commons/utils/types';
import {
  SidenavBundleProvider,
  useHeaderBundle,
} from 'src/providers/PantherLocalizationProvider';
import { useBreadcrumbTruncationContext } from './components/BreadcrumbIA2/BreadcrumbTruncationContext';
import { ConnectedContextBar } from './components/ContextBar/ContextBar';
import { ConnectedFocusHeader } from './components/FocusHeader/FocusHeader';
import { HeaderWrapper, Topbar, TopbarSecondRow } from './components/styles';
import ToastBanner from './components/ToastBanner/ToastBanner';
import { ConnectedUtilityMenu } from './components/UtilityMenu/UtilityMenu';
import { EmptyContextBar, LogoWrapper } from './styles';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import { showInternalView } from 'src/commons/utils/helpers';
import {
  ATC_EXTERNAL_TOGGLE,
  staticImgSrc,
  staticImgSrcWithCache,
  staticInternalImgSrc,
  staticInternalImgSrcWithCache,
} from 'src/commons/constants';
import { isFromBtswWc, isFromTrim } from 'src/commons/utils';
import { useEventListener } from 'src/commons/utils/hooks';
import { ErrorBoundary } from 'src/commons/components/ErrorBoundary';
import SalesAssistantModule from './components/SalesAssistant/SalesAssistantModule';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';

const REFLOW_CONTEXT_BAR_WINDOW_WIDTH = 1000;

export const ToastBannerWrapper: React.VFC<{ isMobile: boolean }> = ({
  isMobile,
}) => {
  const chromeConfig = useChromeConfigContext();
  const toastBanner = chromeConfig.toastBanner;
  if (!toastBanner) {
    return null;
  }
  return <ToastBanner toastBanner={toastBanner} isMobile={isMobile} />;
};

export const ConsoleHeaderContent: React.VFC<{
  pageLayoutType: PAGE_LAYOUT_TYPE;
  logo?: ImageProps;
  internalLogo?: ImageProps;
  isMobile: boolean;
}> = ({ pageLayoutType, logo, internalLogo, isMobile }) => {
  const { config } = useInternalChromeConfigContext();
  const { hasBreadcrumbs } = useBreadcrumbTruncationContext();
  const calcRenderEmptyContextBar = () =>
    window?.innerWidth <= REFLOW_CONTEXT_BAR_WINDOW_WIDTH && hasBreadcrumbs;
  const [shouldRenderEmptyContextBar, setShouldRenderEmptyContextBar] =
    useState(calcRenderEmptyContextBar);
  const { isInternalView } = config;
  const isInternalStandard =
    pageLayoutType === PAGE_LAYOUT_TYPE.INTERNAL_STANDARD || isInternalView;
  const showInternalLogo =
    isInternalStandard &&
    window.sessionStorage.getItem(ATC_EXTERNAL_TOGGLE) !== 'true';
  const [imgSrc, setImgSrc] = useState(
    isFromTrim() || isFromBtswWc()
      ? showInternalLogo
        ? staticInternalImgSrcWithCache
        : staticImgSrcWithCache
      : showInternalLogo
        ? (internalLogo?.src ?? staticInternalImgSrcWithCache)
        : (logo?.src ?? staticImgSrcWithCache),
  );
  const handleError = (e) => {
    e.stopPropagation();
    setImgSrc(showInternalLogo ? staticInternalImgSrc : staticImgSrc);
  };
  const bundle = useHeaderBundle();

  useEventListener({
    eventName: 'resize',
    callback: () => {
      setShouldRenderEmptyContextBar(calcRenderEmptyContextBar);
    },
    element: window,
  });

  if (pageLayoutType === PAGE_LAYOUT_TYPE.FOCUS) {
    return (
      <HeaderWrapper id="headerBar" pageLayoutType={PAGE_LAYOUT_TYPE.FOCUS}>
        <ConnectedFocusHeader />
      </HeaderWrapper>
    );
  }

  if (pageLayoutType === PAGE_LAYOUT_TYPE.FOCUS_NO_HEADER) {
    return null;
  }

  return (
    <HeaderWrapper
      id="headerBar"
      pageLayoutType={
        showInternalView(pageLayoutType, isInternalView)
          ? PAGE_LAYOUT_TYPE.INTERNAL_STANDARD
          : pageLayoutType
      }
      direction="column"
      alignItems="start"
      rowGap="xxxsmall"
    >
      <Topbar
        id="topBar"
        role="presentation"
        isWrapped={shouldRenderEmptyContextBar}
      >
        {!isMobile && (
          <LogoWrapper
            href="/mn"
            {...getTaktAttributes('logo', chromeComponents.HEADER, {
              url: '/mn',
            })}
          >
            <img
              src={imgSrc}
              alt={bundle.getMessage('sidenav-logo-text')}
              onError={handleError}
            />
          </LogoWrapper>
        )}
        {shouldRenderEmptyContextBar ? (
          <EmptyContextBar />
        ) : (
          <SidenavBundleProvider>
            <ConnectedContextBar />
          </SidenavBundleProvider>
        )}
        <ConnectedUtilityMenu />
      </Topbar>
      {shouldRenderEmptyContextBar && (
        <TopbarSecondRow id="secondTopBar" role="presentation">
          <ConnectedContextBar />
        </TopbarSecondRow>
      )}
      <ErrorBoundary name="sales-assistant-drawer">
        <SalesAssistantModule />
      </ErrorBoundary>
    </HeaderWrapper>
  );
};

export const useMobileViewPortHook = ({
  deviceType,
  mobileWebSupport,
}: {
  deviceType: DEVICE_TYPE;
  mobileWebSupport: string;
}): void => {
  useEffect(() => {
    if (deviceType !== DEVICE_TYPE.MOBILE) {
      return;
    }
    const updateViewportTagAttribute = (value: string): void => {
      const viewportTag = document.querySelector('meta[name=viewport]') || null;

      if (viewportTag) {
        viewportTag.setAttribute('content', value);
      }
    };

    if (!mobileWebSupport?.includes('ENABLED')) {
      updateViewportTagAttribute('width=1200, user-scalable=yes');
    } else {
      updateViewportTagAttribute('width=device-width, initial-scale=1');
    }
  }, [deviceType, mobileWebSupport]);
};
